/**
===================================================
Module: frontend.src.components.AuditLog.tsx
Description: User-facing audit log viewer for login/logout activity
             (ISMS A.12.4.1 Event Log Recording)
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
===================================================
*/

import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { apiService } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import TopBar from "./TopBar";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/AuditLog.css";

interface AuditLogEntry {
  id: number;
  event_type: string;
  event_time: string;
  ip_address: string;
  user_agent: string;
  device_info: string;
  session_id: string;
  metadata: Record<string, unknown>;
}

const AuditLog: React.FC = () => {
  const { t } = useTranslation("auditLog");
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const adminUserId = user?.is_superuser ? searchParams.get("admin_user_id") : null;
  const isAdminView = !!adminUserId;

  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [pageSize] = useState(20);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [isExporting, setIsExporting] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [targetUserEmail, setTargetUserEmail] = useState("");

  const addNotification = (message: string, type: "success" | "error" | "info") => {
    const id = Date.now().toString();
    setNotifications((prev) => [...prev, { id, message, type }]);
  };

  const removeNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  };

  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      if (isAdminView && adminUserId) {
        // Admin viewing another user's logs
        const response = await apiService.getAdminAuditLogs({
          user_id: adminUserId,
          page: currentPage,
          page_size: pageSize,
          from: dateFrom || undefined,
          to: dateTo || undefined,
        });
        setLogs(response.logs?.map(l => ({ ...l, user_agent: "", session_id: "", metadata: {} })) || []);
        setTotalPages(response.total_pages || 1);
        setTotalLogs(response.total || 0);
        // Get email from first log entry
        if (response.logs?.length > 0 && response.logs[0].user_email) {
          setTargetUserEmail(response.logs[0].user_email);
        }
      } else {
        const response = await apiService.getAuditLogs({
          page: currentPage,
          page_size: pageSize,
          from: dateFrom || undefined,
          to: dateTo || undefined,
        });
        setLogs(response.logs || []);
        setTotalPages(response.total_pages || 1);
        setTotalLogs(response.total || 0);
      }
    } catch (err) {
      addNotification(
        t("errors.loadFailed", { defaultValue: "ログの読み込みに失敗しました" }),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, dateFrom, dateTo, t, isAdminView, adminUserId]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo]);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await apiService.exportAuditLogs("csv", dateFrom || undefined, dateTo || undefined);
      addNotification(
        t("export.success", { defaultValue: "ログをエクスポートしました" }),
        "success"
      );
    } catch (err) {
      addNotification(
        t("export.failed", { defaultValue: "エクスポートに失敗しました" }),
        "error"
      );
    } finally {
      setIsExporting(false);
    }
  };

  const formatEventTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch {
      return isoString;
    }
  };

  const getEventBadge = (eventType: string) => {
    const config: Record<string, { icon: string; label: string }> = {
      login: {
        icon: "🔑",
        label: t("events.login", { defaultValue: "ログイン" }),
      },
      logout: {
        icon: "🚪",
        label: t("events.logout", { defaultValue: "ログアウト" }),
      },
      login_failed: {
        icon: "⚠",
        label: t("events.loginFailed", { defaultValue: "ログイン失敗" }),
      },
    };
    const { icon, label } = config[eventType] || {
      icon: "📋",
      label: eventType,
    };

    return (
      <span className={`event-badge ${eventType}`}>
        <span className="event-badge-icon">{icon}</span>
        {label}
      </span>
    );
  };

  return (
    <div className="audit-container">
      <div className="audit-content">
        <TopBar />

        <div className="audit-header-section">
          <div className="breadcrumb">
            <span
              className="breadcrumb-link"
              onClick={() => (window.location.href = "/projects")}
            >
              {t("common:home", { defaultValue: "ホーム" })}
            </span>
            <span className="breadcrumb-separator">/</span>
            <span>{t("title", { defaultValue: "ログイン履歴" })}</span>
          </div>

          <div className="audit-title-row">
            <div>
              <h1>{t("title", { defaultValue: "ログイン履歴" })}</h1>
              {isAdminView && targetUserEmail && (
                <p style={{ margin: '4px 0 0', fontSize: '14px', color: 'var(--text-secondary)' }}>
                  {t("adminViewing", { defaultValue: "ユーザー: {{email}}", email: targetUserEmail })}
                </p>
              )}
            </div>
            <div className="audit-filters">
              <label>{t("filters.from", { defaultValue: "開始日" })}</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
              <label>{t("filters.to", { defaultValue: "終了日" })}</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
              <button
                className="audit-export-btn"
                onClick={handleExport}
                disabled={isExporting || isLoading}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
                {isExporting
                  ? t("export.exporting", { defaultValue: "エクスポート中..." })
                  : t("export.csv", { defaultValue: "CSV出力" })}
              </button>
            </div>
          </div>
        </div>

        <div className="audit-main">
          <div className="audit-table-container">
            <table className="audit-table">
              <thead>
                <tr>
                  <th>{t("table.dateTime", { defaultValue: "日時" })}</th>
                  <th>{t("table.eventType", { defaultValue: "イベント" })}</th>
                  <th>
                    {t("table.ipAddress", { defaultValue: "IPアドレス" })}
                  </th>
                  <th>{t("table.device", { defaultValue: "デバイス" })}</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={4} className="audit-empty">
                      {t("common:loading", { defaultValue: "読み込み中..." })}
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="audit-empty">
                      <div className="audit-empty-icon">📋</div>
                      <div className="audit-empty-text">
                        {t("empty", {
                          defaultValue: "ログイン履歴はありません",
                        })}
                      </div>
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => (
                    <tr key={log.id}>
                      <td className="audit-time-cell">
                        {formatEventTime(log.event_time)}
                      </td>
                      <td>{getEventBadge(log.event_type)}</td>
                      <td className="ip-cell">{log.ip_address || "-"}</td>
                      <td className="device-cell" title={log.user_agent}>
                        {log.device_info || "-"}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {!isLoading && logs.length > 0 && (
              <div className="audit-pagination">
                <div className="audit-pagination-info">
                  {t("pagination.showing", {
                    defaultValue: `${totalLogs}件中 ${(currentPage - 1) * pageSize + 1}〜${Math.min(currentPage * pageSize, totalLogs)}件`,
                    total: totalLogs,
                    from: (currentPage - 1) * pageSize + 1,
                    to: Math.min(currentPage * pageSize, totalLogs),
                  })}
                </div>
                <div className="audit-pagination-controls">
                  <button
                    onClick={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    «
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    ‹
                  </button>
                  <span>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    ›
                  </button>
                  <button
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={currentPage === totalPages}
                  >
                    »
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />
    </div>
  );
};

export default AuditLog;
